import React, { useMemo, useState, useCallback } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { useCalendarYearForecast } from 'src/api/rebates/useRebateForecast';
import useRebateAgreementYears from 'src/api/rebates/useRebateAgreementYears';
import { MultiSelect } from 'src/components/common/MultiSelect';
import { useSupplierPaymentsByPeriod } from 'src/api/rebates/useSupplierPayments';
import DebtorsReportTable from '@/components/rebates/DebtorsReportTable';
import DebtorsReportAllocationsTable from '@/components/rebates/DebtorsReportAllocationsTable';
import { SupplierPayment } from 'src/api/rebates/types';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@erp/shared-utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { InfoIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react';
import { deriveCumulativePosition } from 'src/utils/rebateUtils';
import {
  DataSourceSelect,
  type DataSourceType,
} from '@/components/common/DataSourceSelect';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/context/ToastContext';
import {
  ForegroundAgentChat,
  type CollectionsPayload,
} from '@/components/common/ForegroundAgentChat';

const DebtorsReportPage: React.FC = () => {
  const { showToast } = useToast();

  // Year filter state - default to current year
  const currentYear = new Date().getFullYear();
  const [selectedYears, setSelectedYears] = useState<number[]>([currentYear]);
  // Toggle to show closed agreements (default: hidden)
  const [showClosedAgreements, setShowClosedAgreements] = useState(false);
  // Data source selection - default to estimates
  const [dataSource, setDataSource] = useState<DataSourceType>('estimates');

  // Collections dialog state
  const [collectionsDialogOpen, setCollectionsDialogOpen] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Use first selected year for forecast (or current year if none selected)
  const forecastYear =
    selectedYears.length > 0 ? selectedYears[0] : currentYear;

  // Fetch calendar year forecast data
  const {
    data: forecastData,
    isLoading,
    error,
  } = useCalendarYearForecast(forecastYear);

  // Fetch supplier payments for the selected years (use earliest and latest)
  const minYear =
    selectedYears.length > 0 ? Math.min(...selectedYears) : currentYear;
  const maxYear =
    selectedYears.length > 0 ? Math.max(...selectedYears) : currentYear;
  const startDate = `${minYear}-01-01`;
  const endDate = `${maxYear}-12-31`;

  const {
    data: paymentsBySupplierArr,
    isLoading: paymentsLoading,
    isError: paymentsIsError,
  } = useSupplierPaymentsByPeriod(startDate, endDate, !!forecastData);

  const paymentsBySupplier = useMemo(() => {
    const map: Record<string, SupplierPayment[]> = {};
    paymentsBySupplierArr?.forEach((entry) => {
      map[entry.supplierId] = entry.payments as SupplierPayment[];
    });
    return map;
  }, [paymentsBySupplierArr]);

  // Extract suppliers from payments response
  const suppliersFromPayments = useMemo(() => {
    const supplierMap = new Map<string, { id: string; name: string }>();
    paymentsBySupplierArr?.forEach((entry) => {
      entry.payments.forEach((payment) => {
        const supplier = payment.rebateAgreement?.supplier;
        if (supplier && !supplierMap.has(supplier.id)) {
          supplierMap.set(supplier.id, {
            id: supplier.id,
            name: supplier.name,
          });
        }
      });
    });
    return Array.from(supplierMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [paymentsBySupplierArr]);

  // Merge with suppliers from forecast
  const suppliers = useMemo(() => {
    const forecastSuppliers = new Map<string, { id: string; name: string }>();
    forecastData?.estimates?.forEach((estimate) => {
      estimate.breakdowns?.forEach((breakdown) => {
        if (breakdown.supplierId && breakdown.supplierName) {
          forecastSuppliers.set(breakdown.supplierId, {
            id: breakdown.supplierId,
            name: breakdown.supplierName,
          });
        }
      });
    });

    // Merge both sources
    const allSuppliers = new Map(suppliersFromPayments.map((s) => [s.id, s]));
    forecastSuppliers.forEach((supplier, id) => {
      if (!allSuppliers.has(id)) {
        allSuppliers.set(id, supplier);
      }
    });

    return Array.from(allSuppliers.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [suppliersFromPayments, forecastData]);

  // Separate active and closed payments
  const { activePayments, closedPayments } = useMemo(() => {
    const allPayments = suppliers.flatMap(
      (s) => paymentsBySupplier[s.id] || [],
    );

    const active: SupplierPayment[] = [];
    const closed: SupplierPayment[] = [];

    allPayments.forEach((payment) => {
      if (payment.rebateAgreement?.isClosedOut) {
        closed.push(payment);
      } else {
        active.push(payment);
      }
    });

    return { activePayments: active, closedPayments: closed };
  }, [suppliers, paymentsBySupplier]);

  // Combine payments for display (active first, then closed if toggle is on)
  const displayPayments = useMemo(() => {
    if (showClosedAgreements) {
      return [...activePayments, ...closedPayments];
    }
    return activePayments;
  }, [activePayments, closedPayments, showClosedAgreements]);

  // Calculate summary totals for paid, allocated, unallocated, and written off
  // Total Paid, Allocated, and Unallocated update based on toggle (include/exclude closed)
  // Written Off always shows the total from closed agreements regardless of toggle
  const summaryTotals = useMemo(() => {
    const totalPaid = displayPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalAllocated = displayPayments.reduce((sum, p) => {
      const allocatedAmount =
        p.allocatedAmount ??
        p.allocations?.reduce((sum, alloc) => sum + alloc.allocatedAmount, 0) ??
        0;
      return sum + allocatedAmount;
    }, 0);
    const totalUnallocated = totalPaid - totalAllocated;

    // Calculate funds unallocated - sum of unallocatedAmount from each payment
    const fundsUnallocated = displayPayments.reduce((sum, p) => {
      const unallocatedAmount =
        p.unallocatedAmount ?? p.amount - (p.allocatedAmount ?? 0);
      return sum + unallocatedAmount;
    }, 0);

    // Calculate written off amount from closed agreements
    // Written off = sum of writeOffAmount from closureMetadata per unique agreement
    // Subtract unallocated payments since they can still be allocated
    // Only sum underpaid values (positive writeOffAmount), ignore overpaid (negative)
    // Always show total written off regardless of toggle
    // Note: writeOffAmount is stored at the agreement level, so we need to track unique agreements
    const countedAgreements = new Set<string>();
    const totalWrittenOff = closedPayments.reduce((sum, p) => {
      const agreementId = p.rebateAgreement?.id;
      if (!agreementId || countedAgreements.has(agreementId)) {
        return sum; // Skip if no agreement ID or already counted
      }
      countedAgreements.add(agreementId);
      const snapshotData = p.rebateAgreement?.closureMetadata?.snapshotData;
      const writeOffAmount = snapshotData?.writeOffAmount ?? 0;
      const unallocatedPayments = snapshotData?.unallocatedPayments ?? 0;
      // Adjust write-off by subtracting unallocated payments (they can still be allocated)
      // Only add positive values (underpaid), ignore negative values (overpaid)
      const adjustedWriteOff = Math.max(
        0,
        writeOffAmount - unallocatedPayments,
      );
      return sum + adjustedWriteOff;
    }, 0);

    return {
      totalPaid,
      totalAllocated,
      totalUnallocated,
      fundsUnallocated,
      totalWrittenOff,
    };
  }, [displayPayments, closedPayments]);

  // Separate suppliers into active and closed
  const { activeSuppliers, closedSuppliers } = useMemo(() => {
    const active: { id: string; name: string }[] = [];
    const closed: { id: string; name: string }[] = [];

    suppliers.forEach((supplier) => {
      const supplierPayments = paymentsBySupplier[supplier.id] || [];
      const hasClosedAgreement = supplierPayments.some(
        (p) => p.rebateAgreement?.isClosedOut,
      );
      const hasActiveAgreement = supplierPayments.some(
        (p) => !p.rebateAgreement?.isClosedOut,
      );

      // If supplier has no payments, treat as active (show all agreements)
      if (supplierPayments.length === 0) {
        active.push(supplier);
      } else if (hasActiveAgreement) {
        active.push(supplier);
      } else if (hasClosedAgreement && !hasActiveAgreement) {
        closed.push(supplier);
      }
    });

    return { activeSuppliers: active, closedSuppliers: closed };
  }, [suppliers, paymentsBySupplier]);

  // Combine suppliers for display (active first, then closed if toggle is on)
  const displaySuppliers = useMemo(() => {
    if (showClosedAgreements) {
      return [...activeSuppliers, ...closedSuppliers];
    }
    return activeSuppliers;
  }, [activeSuppliers, closedSuppliers, showClosedAgreements]);

  // Derive selected rebate data based on data source selection
  const selectedRebateData = useMemo(() => {
    if (!forecastData) return [];
    if (dataSource === 'estimates') return forecastData.estimates || [];
    if (dataSource === 'reported') return forecastData.reported || [];
    if (dataSource === 'predictions')
      return forecastData.predictions?.['linear-regression'] || [];
    return [];
  }, [dataSource, forecastData]);

  // Calculate owed amounts: YTD actual, YTD predicted, EOY predicted
  // Filter by displaySuppliers to respect the "show closed agreements" toggle
  const owedTotals = useMemo(() => {
    if (!forecastData) {
      return {
        owedYtdActual: 0,
        owedYtdPredicted: 0,
        owedEoyPredicted: 0,
      };
    }

    // Create a Set of supplier IDs to filter by
    const displaySupplierIds = new Set(displaySuppliers.map((s) => s.id));

    // Filter estimates to only include breakdowns from displaySuppliers
    const filteredEstimates = (forecastData.estimates || []).map(
      (estimate) => ({
        ...estimate,
        breakdowns: (estimate.breakdowns || []).filter(
          (bd) => bd.supplierId && displaySupplierIds.has(bd.supplierId),
        ),
      }),
    );

    // Owed YTD Actual: Sum of filtered estimates (cumulative)
    const cumulativeActual = deriveCumulativePosition(filteredEstimates);
    const owedYtdActual = cumulativeActual?.totalRebateAmount || 0;

    // Get current date for YTD calculations
    const today = new Date();

    // Calculate the target date for YTD predicted: next end of month (or today if already end of month)
    const getNextEndOfMonth = (date: Date): Date => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();

      // Get the last day of the current month
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

      // If today is already the end of month, use today
      if (day === lastDayOfMonth) {
        return new Date(year, month, lastDayOfMonth, 23, 59, 59, 999);
      }

      // Otherwise, use the end of the current month
      return new Date(year, month, lastDayOfMonth, 23, 59, 59, 999);
    };

    const ytdPredictedTargetDate = getNextEndOfMonth(today);

    // Find the primary prediction method (prefer linear-regression, fallback to first available)
    const predictionMethods = forecastData.predictions
      ? Object.keys(forecastData.predictions)
      : [];
    const primaryMethod =
      predictionMethods.find((m) => m === 'linear-regression') ||
      predictionMethods[0];

    let owedYtdPredicted = 0;
    let owedEoyPredicted = 0;

    if (primaryMethod && forecastData.predictions?.[primaryMethod]) {
      const predictions = forecastData.predictions[primaryMethod];

      // Filter predictions to only include breakdowns from displaySuppliers
      const filteredPredictions = predictions.map((prediction) => ({
        ...prediction,
        breakdowns: (prediction.breakdowns || []).filter(
          (bd) => bd.supplierId && displaySupplierIds.has(bd.supplierId),
        ),
      }));

      // Owed YTD Predicted: Sum all prediction points up to next end of month (cumulative)
      const ytdPredictions = filteredPredictions.filter(
        (p) => new Date(p.date) <= ytdPredictedTargetDate,
      );
      const cumulativeYtdPredicted = deriveCumulativePosition(ytdPredictions);
      owedYtdPredicted = cumulativeYtdPredicted?.totalRebateAmount || 0;

      // Owed EOY Predicted: Sum all prediction points for the entire year (cumulative)
      const cumulativeEoyPredicted =
        deriveCumulativePosition(filteredPredictions);
      owedEoyPredicted = cumulativeEoyPredicted?.totalRebateAmount || 0;
    }

    return {
      owedYtdActual,
      owedYtdPredicted,
      owedEoyPredicted,
    };
  }, [forecastData, displaySuppliers]);

  // Get list of suppliers with outstanding balances for collections
  const suppliersWithOutstandingBalances = useMemo(() => {
    return displaySuppliers.filter((supplier) => {
      const supplierPayments = paymentsBySupplier[supplier.id] || [];
      const totalPaid = supplierPayments.reduce((sum, p) => sum + p.amount, 0);
      // Check if there's an outstanding balance (owed more than paid)
      // This is a simplified check - in reality you'd compare against actual owed amounts
      return totalPaid > 0 || supplierPayments.length === 0;
    });
  }, [displaySuppliers, paymentsBySupplier]);

  // Handle collections button click
  const handleCollectionsConfirm = useCallback(async () => {
    setCollectionsLoading(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const vendorCount = suppliersWithOutstandingBalances.length;
    const vendorNames = suppliersWithOutstandingBalances
      .slice(0, 3)
      .map((s) => s.name)
      .join(', ');
    const andMore =
      vendorCount > 3 ? ` and ${vendorCount - 3} other vendors` : '';

    showToast({
      title: 'Collection Agents Dispatched',
      message: `Successfully initiated collection outreach to ${vendorNames}${andMore}. You will receive updates as vendors respond.`,
      color: 'success',
    });

    setCollectionsLoading(false);
    setCollectionsDialogOpen(false);
  }, [suppliersWithOutstandingBalances, showToast]);

  // Handle AI-triggered collections
  const handleAICollections = useCallback(
    (payload: CollectionsPayload) => {
      const vendorNames = payload.vendorNames.slice(0, 3).join(', ');
      const andMore =
        payload.vendorNames.length > 3
          ? ` and ${payload.vendorNames.length - 3} other vendors`
          : '';

      showToast({
        title: 'Collection Agents Dispatched',
        message: `${payload.message} Contacting: ${vendorNames}${andMore}.`,
        color: 'success',
      });
    },
    [showToast],
  );

  const hasError = error && !forecastData;

  if (hasError) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Debtors Report</h1>
          </div>
          <div className="text-center py-12">
            <h2 className="text-lg text-red-600">
              Error loading debitors data
            </h2>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (paymentsIsError) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Debtors Report</h1>
          </div>
          <div className="text-center py-12">
            <h2 className="text-lg text-red-600">Error loading payment data</h2>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Debtors Report</h1>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-sm text-muted-foreground">
                Supplier payment status and outstanding balances
              </p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-sm">
                      Closed agreements are excluded from status cards and
                      totals by default. Use the toggle below to view closed
                      agreements at the bottom of the table.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">
                Data Source:
              </Label>
              <DataSourceSelect value={dataSource} onChange={setDataSource} />
            </div>
            <div className="flex items-start gap-2">
              <Switch
                id="show-closed"
                checked={showClosedAgreements}
                onCheckedChange={setShowClosedAgreements}
              />
              <Label htmlFor="show-closed" className="text-sm cursor-pointer">
                Show closed agreements
              </Label>
            </div>
            {availableYears && availableYears.length > 0 && (
              <MultiSelect
                label="Year:"
                labelPosition="left"
                options={availableYears
                  .sort((a, b) => b - a)
                  .map((year) => ({
                    id: year.toString(),
                    label: year.toString(),
                    value: year,
                  }))}
                value={selectedYears
                  .map((year) => ({
                    id: year.toString(),
                    label: year.toString(),
                    value: year,
                  }))
                  .filter((opt) =>
                    availableYears.includes(opt.value as number),
                  )}
                onChange={(options) => {
                  setSelectedYears(
                    options
                      .map((opt) => opt.value as number)
                      .filter((y) => !isNaN(y)),
                  );
                }}
                placeholder="Select years..."
                searchPlaceholder="Search years..."
                maxChips={3}
                size="sm"
                shouldAutoSort={false}
                liveUpdate={false}
              />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setCollectionsDialogOpen(true)}
                    disabled={suppliersWithOutstandingBalances.length === 0}
                    className="gap-2"
                  >
                    <PaperPlaneTiltIcon className="h-4 w-4" />
                    Collections
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Send collection outreach to{' '}
                    {suppliersWithOutstandingBalances.length} vendors
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Summary Tables */}
        {suppliers.length > 0 &&
          paymentsBySupplier &&
          forecastData &&
          !isLoading &&
          !paymentsLoading && (
            <>
              <div className="mb-6 flex gap-4 flex-wrap">
                {/* Payment Summary Table */}
                <div className="bg-black-50 border-2 border-black-200 rounded-lg p-3 min-w-[260px]">
                  <h3 className="text-sm font-semibold mb-2 text-muted-foreground">
                    Funds Received
                  </h3>
                  <Table className="w-auto">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs font-semibold">
                          Status
                        </TableHead>
                        <TableHead className="h-8 text-xs font-semibold text-right">
                          Amount
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Total Received
                        </TableCell>
                        <TableCell className="py-1 text-xs text-right text-green-700">
                          {formatCurrency(summaryTotals.totalPaid)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Total Allocated
                        </TableCell>
                        <TableCell className="py-1 text-xs text-right text-blue-700">
                          {formatCurrency(summaryTotals.totalAllocated)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Unallocated
                        </TableCell>
                        <TableCell
                          className={`py-1 text-xs text-right ${
                            summaryTotals.totalUnallocated > 0
                              ? 'text-orange-600'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {formatCurrency(summaryTotals.totalUnallocated)}
                        </TableCell>
                      </TableRow>
                      {/* Funds Unallocated - Commented out as it appears to be redundant with "Unallocated" above.
                          Both represent unallocated funds: "Unallocated" = totalPaid - totalAllocated (aggregate),
                          "Funds Unallocated" = sum of each payment's unallocatedAmount field.
                          They should be the same value, so keeping only "Unallocated" to avoid confusion. */}
                      {/* <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Funds Unallocated
                        </TableCell>
                        <TableCell
                          className={`py-1 text-xs text-right ${
                            summaryTotals.fundsUnallocated > 0
                              ? 'text-red-600'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {formatCurrency(summaryTotals.fundsUnallocated)}
                        </TableCell>
                      </TableRow> */}
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Written Off
                        </TableCell>
                        <TableCell className="py-1 text-xs text-right text-slate-600">
                          {formatCurrency(summaryTotals.totalWrittenOff)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Owed Summary Table */}
                <div className="bg-black-50 border-2 border-black-200 rounded-lg p-3 min-w-[260px]">
                  <h3 className="text-sm font-semibold mb-2 text-muted-foreground">
                    Rebates Owed
                  </h3>
                  <Table className="w-auto">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs font-semibold">
                          Period
                        </TableHead>
                        <TableHead className="h-8 text-xs font-semibold text-right">
                          Amount
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Owed YTD Actual
                        </TableCell>
                        <TableCell className="py-1 text-xs text-right">
                          {formatCurrency(owedTotals.owedYtdActual)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Owed YTD Predicted
                        </TableCell>
                        <TableCell className="py-1 text-xs text-right">
                          {formatCurrency(owedTotals.owedYtdPredicted)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="py-1 text-xs font-medium">
                          Owed EOY Predicted
                        </TableCell>
                        <TableCell className="py-1 text-xs text-right">
                          {formatCurrency(owedTotals.owedEoyPredicted)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}

        {/* Debtors Report Table */}
        {isLoading || !forecastData ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium">
                Debtors Report
              </CardTitle>
              <CardDescription>
                Supplier payment status and outstanding balances
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TableSkeleton rows={5} columns={8} />
            </CardContent>
          </Card>
        ) : paymentsLoading ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium">
                Debtors Report
              </CardTitle>
              <CardDescription>
                Supplier payment status and outstanding balances
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TableSkeleton rows={5} columns={8} />
            </CardContent>
          </Card>
        ) : displaySuppliers.length > 0 &&
          paymentsBySupplier &&
          forecastData ? (
          <>
            <DebtorsReportTable
              suppliers={displaySuppliers}
              paymentsData={displayPayments}
              rebateData={selectedRebateData}
              showClosedAgreements={showClosedAgreements}
              dataSource={dataSource}
            />
            <div className="mt-8">
              <DebtorsReportAllocationsTable
                suppliers={displaySuppliers}
                paymentsData={displayPayments}
                rebateData={selectedRebateData}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            {!isLoading &&
            !paymentsLoading &&
            displaySuppliers.length === 0 &&
            selectedYears.length === 1 &&
            selectedYears[0] === currentYear &&
            availableYears?.includes(currentYear - 1) ? (
              <div className="space-y-4">
                <h2 className="text-lg text-muted-foreground">
                  No debtors data available for {currentYear}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Would you like to view data for {currentYear - 1}?
                </p>
                <Button
                  onClick={() => setSelectedYears([currentYear - 1])}
                  variant="default"
                >
                  View {currentYear - 1} Data
                </Button>
              </div>
            ) : (
              <h2 className="text-lg text-muted-foreground">
                No debtors data available
              </h2>
            )}
          </div>
        )}

        {/* Collections Confirm Dialog */}
        <ConfirmDialog
          open={collectionsDialogOpen}
          onClose={() => setCollectionsDialogOpen(false)}
          onConfirm={handleCollectionsConfirm}
          title="Initiate Collection Outreach"
          message={`This will dispatch AI collection agents to contact ${suppliersWithOutstandingBalances.length} vendor(s) regarding outstanding rebate payments.\n\nVendors to contact:\n${suppliersWithOutstandingBalances
            .slice(0, 5)
            .map((s) => `- ${s.name}`)
            .join(
              '\n',
            )}${suppliersWithOutstandingBalances.length > 5 ? `\n...and ${suppliersWithOutstandingBalances.length - 5} more` : ''}\n\nThe agents will send professional collection notices and track responses.`}
          confirmText="Send Collection Notices"
          loading={collectionsLoading}
          dangerous={false}
        />

        {/* AI Chat Assistant */}
        <ForegroundAgentChat
          moduleKey="rebates"
          currentPage="/rebates/debtors"
          onTriggerCollections={handleAICollections}
        />
      </div>
    </DashboardLayout>
  );
};

export default DebtorsReportPage;
